import React, { useState, useRef, useEffect, useMemo, useCallback, memo } from "react";
import { createPortal } from "react-dom";
import { OptionType } from "@shared/types";
import HStack from "../layout/HStack";
import { Loader } from "./Loader";
import { useTranslation } from "react-i18next";

type Props = {
  textSearch?: string;
  options: OptionType[];
  onChangeInput: (value: string) => void;
  onSelectOption?: (value: string | number) => void;
  placeholder?: string;
  className?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  maxHeightDropdown?: number;
  widthDropdown?: number;
  isHighlightBorder?: boolean;
  isLoading?: boolean;
} & React.InputHTMLAttributes<HTMLInputElement>;

const Combobox = ({
  options,
  textSearch,
  onChangeInput,
  onSelectOption,
  placeholder = "Search...",
  className,
  leftIcon,
  maxHeightDropdown = 300,
  widthDropdown,
  isHighlightBorder = false,
  onFocus,
  onBlur,
  isLoading = false,
  ...props
}: Props) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    showAbove: false,
    alignRight: false,
  });

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleSelectOption = (optionValue: string) => {
    onSelectOption?.(optionValue);
    closeDropdown();
    inputRef.current?.blur();
  };

  const calculateDropdownPosition = useCallback(() => {
    if (!containerRef.current) return;

    const container = containerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const dropdownWidth = widthDropdown || container.width;

    let estimatedHeight = maxHeightDropdown;
    if (options && options.length > 0) {
      const optionHeight = 32;
      const estimatedContentHeight = options.length * optionHeight;
      estimatedHeight = Math.min(estimatedContentHeight + 16, maxHeightDropdown);
    }

    const spaceBelow = viewportHeight - container.bottom;
    const spaceAbove = container.top;
    const showAbove = spaceBelow < estimatedHeight && spaceAbove >= estimatedHeight;

    let left = container.left;
    const alignRight = left + dropdownWidth > viewportWidth;

    if (alignRight) {
      left = container.right - dropdownWidth;
    }

    left = Math.max(8, Math.min(left, viewportWidth - dropdownWidth - 8));

    let top;
    if (showAbove) {
      top = container.top - estimatedHeight;
    } else {
      top = container.bottom;
    }

    const minTop = 8;
    const maxTop = viewportHeight - estimatedHeight - 8;
    top = Math.max(minTop, Math.min(top, maxTop));

    setDropdownPosition({
      top,
      left,
      width: dropdownWidth,
      showAbove,
      alignRight,
    });
  }, [widthDropdown, maxHeightDropdown, options]);

  const handleInputFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (!isOpen) {
        calculateDropdownPosition();
        setIsOpen(true);
      }
      // Call the onFocus prop if provided
      onFocus?.(e);
    },
    [isOpen, onFocus, calculateDropdownPosition]
  );

  const handleInputBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      // Call the onBlur prop if provided
      onBlur?.(e);
    },
    [onBlur]
  );

  const dropdownContent = useMemo((): React.ReactNode | null => {
    if (!isOpen || !textSearch) return null;

    const dropdownStyle = {
      position: "fixed" as const,
      top: dropdownPosition.top,
      left: dropdownPosition.left,
      width: dropdownPosition.width,
      zIndex: 99999,
      maxHeight: maxHeightDropdown,
    };

    return createPortal(
      <ul
        data-combobox-dropdown
        className="overflow-auto rounded-[4px] border bg-white shadow-elevationShadow4 dark:bg-black dark:border-none"
        style={dropdownStyle}
      >
        {options && options.length > 0 ? (
          options.map((option) => (
            <li
              key={option.value as string}
              onClick={(e) => {
                e.stopPropagation();
                handleSelectOption(option.value as string);
              }}
              className={`px-3 py-2 text-xs cursor-pointer hover:bg-extraLightGray dark:hover:bg-deepGray dark:text-white`}
            >
              <p className=" text-xs font-semibold">{option.label}</p>
              <p className="text-mediumGray text-[10px]">{option.value}</p>
            </li>
          ))
        ) : !isLoading ? (
          <li className="px-3 py-2 text-xs text-mediumDarkGray dark:text-secondaryGray text-center">
            {t("No data available")}
          </li>
        ) : null}
        {isLoading ? (
          <HStack className="w-full p-2 justify-center items-center">
            <Loader texts={[t("Loading...")]} />
          </HStack>
        ) : null}
      </ul>,
      document.body
    );
  }, [isOpen, textSearch, options, dropdownPosition, maxHeightDropdown, handleSelectOption]);

  useEffect(() => {
    if (isOpen) {
      const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as Node;
        if (containerRef.current?.contains(target)) {
          return;
        }
        const dropdownElements = document.querySelectorAll("[data-combobox-dropdown]");
        for (const element of dropdownElements) {
          if (element.contains(target)) {
            return;
          }
        }
        closeDropdown();
      };
      const handleUpdate = () => calculateDropdownPosition();

      window.addEventListener("scroll", handleUpdate, true);
      window.addEventListener("resize", handleUpdate);
      window.addEventListener("mousedown", handleClickOutside);

      return () => {
        window.removeEventListener("scroll", handleUpdate, true);
        window.removeEventListener("resize", handleUpdate);
        window.removeEventListener("mousedown", handleClickOutside);
      };
    }
    return undefined;
  }, [isOpen, calculateDropdownPosition, closeDropdown]);

  return (
    <div
      ref={containerRef}
      className={`relative flex items-center gap-1 w-full rounded-[4px] border-[1px] border-solid border-mediumGray bg-white px-[10px] py-[6px] dark:bg-darkBackgroundColor dark:border-midGray ${
        isHighlightBorder ? "border-b-[2px] border-b-primaryBlue" : ""
      } ${className || ""}`}
    >
      {leftIcon ? leftIcon : null}
      <input
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        value={textSearch}
        onChange={(e) => onChangeInput(e.target.value)}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        className="w-full h-full outline-none dark:bg-darkBackgroundColor placeholder:text-mediumDarkGray dark:placeholder:text-secondaryGray"
        {...props}
      />

      {dropdownContent}
    </div>
  );
};

export default memo(Combobox);
